/**
 * useDeckFiltering Hook
 * Filtert und sortiert Deck-Karten basierend auf verschiedenen Kriterien.
 *
 * @param {Object} options
 * @param {Array} options.cards - Die Karten des Decks
 * @param {string} options.searchTerm - Suchbegriff für Kartenname
 * @param {number|null} options.highlightedCmc - CMC-Filter (null = kein Filter)
 * @param {string} options.sortBy - Sortierkriterium: 'name', 'cmc', 'power', 'toughness'
 *
 * @returns {Array} Gefilterte und sortierte Karten
 */
import { useMemo } from 'react';
import { splitSearchWords, textContainsAllWords } from '../../../utils/wordSearch';

// ========== KONFIGURATIONSKONSTANTEN ==========
// CMC-Grenze für "7+" Kategorie
export const CMC_MAX_DISPLAY = 7;

// Sortieroptionen
export const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'cmc', label: 'Manakosten' },
  { value: 'power', label: 'Stärke' },
  { value: 'toughness', label: 'Widerstand' },
];

/**
 * Haupthook für Deck-Filterung und Sortierung
 */
export function useDeckFiltering({
  cards = [],
  searchTerm = '',
  highlightedCmc = null,
  sortBy = 'name',
}) {
  const filteredAndSortedCards = useMemo(() => {
    if (!cards || cards.length === 0) return [];

    let result = [...cards];

    // 1. Suchfilter (Name) — wortweise UND, Reihenfolge egal
    if (searchTerm && searchTerm.trim()) {
      const words = splitSearchWords(searchTerm);
      result = result.filter((card) =>
        textContainsAllWords(card.name || card.cardName || '', words)
      );
    }

    // 2. CMC-Filter (durch Manakurven-Klick)
    if (highlightedCmc !== null) {
      result = result.filter((card) => {
        const cardCmc = Math.floor(card.cmc || 0);
        if (highlightedCmc >= CMC_MAX_DISPLAY) {
          return cardCmc >= CMC_MAX_DISPLAY;
        }
        return cardCmc === highlightedCmc;
      });
    }

    // 3. Sortierung
    result.sort((a, b) => {
      switch (sortBy) {
        case 'cmc':
          return (a.cmc || 0) - (b.cmc || 0);
        case 'power': {
          const pa = parseInt(a.power, 10) || 0;
          const pb = parseInt(b.power, 10) || 0;
          return pb - pa; // Höhere Stärke zuerst
        }
        case 'toughness': {
          const ta = parseInt(a.toughness, 10) || 0;
          const tb = parseInt(b.toughness, 10) || 0;
          return tb - ta; // Höherer Widerstand zuerst
        }
        case 'name':
        default: {
          const nameA = (a.name || a.cardName || '').toLowerCase();
          const nameB = (b.name || b.cardName || '').toLowerCase();
          return nameA.localeCompare(nameB, 'de');
        }
      }
    });

    return result;
  }, [cards, searchTerm, highlightedCmc, sortBy]);

  return filteredAndSortedCards;
}

// ========== MANA-FARBEN ==========
const COLORED_MANA_SYMBOLS = new Set(['W', 'U', 'B', 'R', 'G']);

/**
 * Zählt farbige Manasymbole (WUBRG) aus einem manaCost-String.
 * z.B. '{2}{B}{B}{R}' → { B: 2, R: 1 }
 */
function countColoredSymbols(manaCost) {
  const counts = {};
  if (!manaCost) return counts;
  const regex = /\{([^}]+)\}/g;
  let match;
  while ((match = regex.exec(manaCost)) !== null) {
    const sym = match[1].toUpperCase();
    if (COLORED_MANA_SYMBOLS.has(sym)) {
      counts[sym] = (counts[sym] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Berechnet alle Statistiken für ein Deck in einem einzigen Durchlauf.
 * Wird von ManaCurve, ManaBaseSection und HypergeometricStats genutzt.
 */
export function useDeckStats(cards = []) {
  return useMemo(() => {
    let totalCards = 0;
    let landCount = 0;
    let mdfcCount = 0;
    let basicLandCount = 0;
    let cmcSum = 0;
    let cmcQty = 0;

    const cmcCounts = {};
    const manaColorCounts = { W: 0, U: 0, B: 0, R: 0, G: 0 };
    const manaProductionCounts = { W: 0, U: 0, B: 0, R: 0, G: 0 };

    // Erster Durchlauf: CMC, Manakosten, Land-Zählung, Produktion sammeln
    const productionEntries = []; // { produced: string[], qty: number }

    for (const card of cards) {
      const qty = card.quantity || 1;
      totalCards += qty;

      const types = card.types || [];
      const superTypes = card.superTypes || [];
      let isLand = types.some((t) => String(t).toLowerCase() === 'land');
      const isBasic = superTypes.some((st) => String(st).toLowerCase() === 'basic');
      let isMDFC = false;

      if (isLand) {
        isMDFC = card.imageUrlBack != null;
        isLand = !isMDFC;
      }

      if (isLand) {
        landCount += qty;
        if (isBasic) basicLandCount += qty;
      } else if (isMDFC) {
        // MDFCs zählen als Land-Slot mit, bleiben für Kurve und Farbbedarf aber Nicht-Länder
        landCount += qty;
        mdfcCount += qty;
      }

      // Mana-Produktion vormerken (wird im zweiten Durchlauf gefiltert)
      const produced = card.manaProduced;
      if (Array.isArray(produced) && produced.length > 0) {
        productionEntries.push({ produced, qty });
      }

      // Lands überspringen für CMC und Manakosten-Verteilung
      if (isLand) continue;

      // CMC-Kurve
      const cmc = Math.min(Math.floor(card.cmc || 0), CMC_MAX_DISPLAY);
      const key = cmc >= CMC_MAX_DISPLAY ? '7+' : String(cmc);
      cmcCounts[key] = (cmcCounts[key] || 0) + qty;

      // Ø-Manakosten: ungekappt (CMC_MAX_DISPLAY betrifft nur die Kurven-Darstellung)
      cmcSum += (card.cmc || 0) * qty;
      cmcQty += qty;

      // Manakosten-Verteilung (nur farbige Symbole WUBRG)
      const colorSymbols = countColoredSymbols(card.manaCost);
      for (const [sym, count] of Object.entries(colorSymbols)) {
        manaColorCounts[sym] += count * qty;
      }
    }

    // Zweiter Durchlauf: Mana-Produktion, 5-Farben-Produzenten filtern
    // Karten die ALLE 5 Farben produzieren (z.B. Command Tower) sollen nur
    // die Farben zählen, die tatsächlich in den Manakosten vorkommen.
    const activeColors = new Set(
      Object.entries(manaColorCounts)
        .filter(([, count]) => count > 0)
        .map(([sym]) => sym)
    );

    for (const { produced, qty } of productionEntries) {
      const upperProduced = produced.map((s) => String(s).toUpperCase()).filter((s) => COLORED_MANA_SYMBOLS.has(s));
      const producesAll5 = upperProduced.length >= 5;

      for (const sym of upperProduced) {
        // Wenn die Karte alle 5 Farben produziert, nur Deck-relevante Farben zählen
        if (producesAll5 && !activeColors.has(sym)) continue;
        manaProductionCounts[sym] += qty;
      }
    }

    return {
      totalCards,
      landCount,
      mdfcCount,
      basicLandCount,
      cmcCounts,
      manaColorCounts,
      manaProductionCounts,
      avgCmc: cmcQty > 0 ? cmcSum / cmcQty : null,
    };
  }, [cards]);
}

/**
 * Ermittelt den Haupttyp einer Karte für die Typen-Gruppierung
 */
export function getPrimaryType(card) {
  const types = card.types || [];
  
  // Prioritätsreihenfolge für Typen
  const typePriority = [
    'Creature',
    'Planeswalker',
    'Artifact',
    'Enchantment',
    'Instant',
    'Sorcery',
    'Land',
  ];

  for (const priority of typePriority) {
    if (types.some((t) => t.toLowerCase() === priority.toLowerCase())) {
      return priority;
    }
  }

  return 'Other';
}

export default useDeckFiltering;
